import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight } from 'lucide-react';
import { useI18n } from '../hooks/useI18n';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper to get image URL
const getImageUrl = (item) => {
  if (!item.image_url) return null;
  if (item.image_url.startsWith('http')) return item.image_url;
  if (item.image_url.startsWith('/uploads')) return `${API_URL}${item.image_url}`;
  return item.image_url;
};

export default function NewsEvents({ newsEvents = [] }) {
  const { t } = useI18n();

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Only show 3 most recent items on homepage
  const items = newsEvents.slice(0, 3);
  if (items.length === 0) return null;

  return (
    <section className="w-full px-2 sm:px-4 md:px-8 lg:px-16 py-8 md:py-12 bg-[#FBFAF0]" data-testid="news-events">
      <div className="container-cish max-w-7xl mx-auto">
        {/* Section heading - matches CISH */}
        <div className="flex items-center gap-2 md:gap-3 mb-6 md:mb-10">
          <img src="/icons/newspaper.svg" alt="" className="w-5 h-5 md:w-6 md:h-6" aria-hidden="true" />
          <div className="text-lg md:text-[22px] font-bold" style={{ color: '#1B5E20' }}>
            NEWS & EVENTS
          </div>
        </div>

        {/* News cards grid - Show only 3 cards */}
        <div className="grid gap-6 md:grid-cols-3 sm:grid-cols-2 grid-cols-1">
          {items.map((item, index) => (
            <Link
              key={item.id || index}
              to={`/events/${item.id}`}
              className="bg-white rounded-lg overflow-hidden flex flex-col shadow-md hover:shadow-lg transition group"
              data-testid={`news-card-${index}`}
            >
              {/* Image - Title is now BELOW the image */}
              <div className="relative">
                {getImageUrl(item) ? (
                  <img
                    src={getImageUrl(item)}
                    alt={item.title}
                    className="w-full h-48 md:h-56 object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-full h-48 md:h-56 bg-gradient-to-br from-[#E8F5E9] to-[#C8E6C9] flex items-center justify-center">
                    <Calendar className="w-16 h-16 text-[#0E6D2B]/30" />
                  </div>
                )}
                {/* Date Badge */}
                {item.event_date && (
                  <div className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-sm">
                    <div className="text-xs font-bold text-[#0E6D2B]">
                      {new Date(item.event_date).toLocaleDateString('en-IN', { day: 'numeric' })}
                    </div>
                    <div className="text-[10px] text-gray-600 uppercase">
                      {new Date(item.event_date).toLocaleDateString('en-IN', { month: 'short' })}
                    </div>
                  </div>
                )}
              </div>

              {/* Content - Title Below Image */}
              <div className="p-4 flex flex-col flex-1">
                {/* Title */}
                <h3 
                  className="font-bold text-sm md:text-base text-[#1B5E20] mb-2 line-clamp-2 group-hover:text-[#0E6D2B] transition-colors"
                >
                  {item.title}
                </h3>

                {/* Date */}
                {item.event_date && (
                  <div className="flex items-center text-gray-500 text-xs mb-2">
                    <Calendar className="w-3.5 h-3.5 mr-1" aria-hidden="true" />
                    <span>{formatDate(item.event_date)}</span>
                  </div>
                )}

                {/* Excerpt */}
                {item.excerpt && (
                  <p className="text-gray-600 text-sm line-clamp-2 flex-1">
                    {item.excerpt}
                  </p>
                )}

                {/* Read More */}
                <div className="flex items-center text-[#0E6D2B] font-medium text-sm mt-3 group-hover:gap-2 transition-all">
                  Read More
                  <ArrowRight className="w-4 h-4 ml-1" aria-hidden="true" />
                </div>
              </div>
            </Link>
          ))}
        </div>

        {/* View More button - Links to Events Page */}
        {newsEvents.length > 3 && (
          <div className="flex justify-center mt-8 md:mt-10">
            <Link 
              to="/events"
              className="bg-[#0E6D2B] hover:bg-[#0a5a23] text-white px-8 py-3 font-semibold text-sm rounded-lg transition-colors flex items-center gap-2"
            >
              VIEW MORE
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
